/**
 * Documentation › Pinned instructions: the team's documents every agent session starts with, the
 * way steering files work — kept in DevPulse instead of in each repository.
 *
 * The server puts them before the prompt of every session DevPulse starts (the desktop app, the
 * prompt queue, loops and team work), and the session-start hook adds them to the sessions a person
 * opens in Claude Code. A document is pinned here, or from its right-click menu in Documentation.
 */

import { useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FilePlus2, Info, Pin, PinOff, Upload } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { atLeast, getSession } from '../lib/auth'
import { EmptyState, ErrorBanner, Panel, Skeleton } from '../components/primitives'
import { isDesktop } from '../lib/desktop'
import { readLocalInstructions, saveLocalInstructions, WITH_EVERY_PROMPT_MAX } from '../lib/localInstructions'

/** The folder new pinned files go in. Any document can be pinned wherever it is. */
const PINNED_FOLDER = 'pinned'
/** The most of the pinned text the server sends before a prompt (DocsService.MaxPinnedChars). */
const MAX_PINNED_BYTES = 24_000

/**
 * "Só nesta máquina" (docs/refactor/21-steering-local-e-compartilhado.md): instructions that go with
 * every prompt sent from this computer, for whoever wrote them — kept in this app, never sent to Vephus.
 * The project's (below) go for every AI of the project.
 */
function ThisMachineInstructions() {
  const { t } = useI18n()
  const desktop = isDesktop()
  const [text, setText] = useState(() => readLocalInstructions())
  const [saved, setSaved] = useState(false)
  return (
    <Panel title={t('pinned.local.title')} subtitle={t(desktop ? 'pinned.local.hint' : 'pinned.local.webHint')}>
      {desktop ? (
        <div className="stack stack--tight">
          <textarea
            className="input"
            rows={5}
            value={text}
            maxLength={WITH_EVERY_PROMPT_MAX}
            placeholder={t('pinned.local.placeholder')}
            aria-label={t('pinned.local.title')}
            onChange={(event) => {
              setText(event.target.value)
              setSaved(false)
            }}
          />
          <div className="row row--tight">
            <button type="button" className="btn btn--sm btn--primary" onClick={() => { saveLocalInstructions(text); setSaved(true) }}>
              {t('common.save')}
            </button>
            {saved && <span className="tiny muted">{t('pinned.local.saved')}</span>}
          </div>
        </div>
      ) : null}
    </Panel>
  )
}

export default function PinnedInstructions() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const canWrite = atLeast(getSession()?.role, 'Developer')
  const [explain, setExplain] = useState(false)
  const [name, setName] = useState('')
  const [choice, setChoice] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const upload = useRef<HTMLInputElement>(null)

  const { data: tree, isLoading } = useQuery({ queryKey: ['docs', 'tree'], queryFn: api.docs.tree })
  const pinned = useMemo(() => (tree?.docs ?? []).filter((d) => d.pinned).sort((a, b) => a.path.localeCompare(b.path)), [tree])
  const others = useMemo(() => (tree?.docs ?? []).filter((d) => !d.pinned).sort((a, b) => a.path.localeCompare(b.path)), [tree])
  const total = pinned.reduce((sum, d) => sum + d.sizeBytes, 0)

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['docs'] })
  const fail = (error: unknown) => setProblem(error instanceof ApiError && error.message ? error.message : t('error.generic'))

  const toggle = useMutation({
    mutationFn: ({ id, pin }: { id: string; pin: boolean }) => api.docs.setPinned(id, pin),
    onSuccess: () => {
      setChoice('')
      setProblem(null)
      refresh()
    },
    onError: fail,
  })

  /** The folder new pinned files go in, made the first time. */
  const folderId = async () => {
    const existing = tree?.folders.find((f) => f.parentId === null && f.name === PINNED_FOLDER)
    return existing ? existing.id : (await api.docs.createFolder({ name: PINNED_FOLDER, parentId: null })).id
  }

  const create = useMutation({
    mutationFn: async () => {
      const base = name.trim().replace(/\.md$/i, '')
      const doc = await api.docs.create({ folderId: await folderId(), name: `${base}.md`, content: `# ${base}\n\n` })
      await api.docs.setPinned(doc.id, true)
      return doc
    },
    onSuccess: (doc) => {
      setName('')
      refresh()
      navigate(`/project/docs?doc=${doc.id}`)
    },
    onError: fail,
  })

  const send = useMutation({
    mutationFn: async (files: File[]) => {
      const results = await api.docs.upload(files.map((file) => ({ file, path: file.name })), PINNED_FOLDER)
      for (const result of results.filter((r) => r.status !== 'rejected')) {
        const doc = await api.docs.byPath(result.path)
        await api.docs.setPinned(doc.id, true)
      }
      const refused = results.filter((r) => r.status === 'rejected')
      if (refused.length > 0) throw new ApiError(refused.map((r) => `${r.path}: ${r.reason ?? ''}`).join(' · '), 422)
    },
    onSuccess: () => {
      setProblem(null)
      refresh()
    },
    onError: (error) => {
      refresh()
      fail(error)
    },
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (name.trim()) create.mutate()
  }
  const onFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.target.files ?? [])].filter((f) => /\.md$/i.test(f.name))
    if (files.length > 0) send.mutate(files)
    event.target.value = ''
  }

  return (
    <div className="stack">
      <ThisMachineInstructions />
      <div className="row row--tight">
        <p className="muted small" style={{ margin: 0, maxWidth: 760 }}>{t('pinned.intro')}</p>
        <button
          type="button"
          className="btn btn--ghost btn--icon btn--sm"
          aria-label={t('pinned.whatIs')}
          title={t('pinned.explain')}
          aria-expanded={explain}
          onClick={() => setExplain((v) => !v)}
        >
          <Info size={15} />
        </button>
      </div>
      {explain && (
        <div className="notice small" role="note">
          <strong>{t('pinned.whatIs')}</strong>
          <p style={{ margin: '6px 0 0' }}>{t('pinned.explain')}</p>
          <p style={{ margin: '6px 0 0' }}>{t('pinned.explainWhere')}</p>
        </div>
      )}

      {problem && <ErrorBanner message={problem} />}

      <Panel title={t('pinned.listTitle', { count: pinned.length })}>
        {isLoading && <Skeleton height={80} />}
        {!isLoading && pinned.length === 0 && <EmptyState title={t('pinned.emptyTitle')} body={t('pinned.emptyBody')} />}
        {pinned.length > 0 && (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('pinned.file')}</th>
                  <th>{t('pinned.size')}</th>
                  <th>{t('pinned.updated')}</th>
                  <th aria-label={t('pinned.actions')} />
                </tr>
              </thead>
              <tbody>
                {pinned.map((doc) => (
                  <tr key={doc.id}>
                    <td>
                      <Link to={`/project/docs?doc=${doc.id}`} className="mono small">{doc.path}</Link>
                    </td>
                    <td className="small">{(doc.sizeBytes / 1024).toFixed(1)} KB</td>
                    <td className="small muted">
                      {new Date(doc.updatedAt).toLocaleString(locale)}
                      {doc.updatedBy ? ` · ${doc.updatedBy}` : ''}
                    </td>
                    <td className="row row--end">
                      {canWrite && (
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => toggle.mutate({ id: doc.id, pin: false })} disabled={toggle.isPending}>
                          <PinOff size={13} /> {t('pinned.unpin')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pinned.length > 0 && (
          <p className={`tiny ${total > MAX_PINNED_BYTES ? 'text-warn' : 'muted'}`} style={{ margin: '8px 0 0' }}>
            {t(total > MAX_PINNED_BYTES ? 'pinned.tooLong' : 'pinned.total', { kb: (total / 1024).toFixed(1), max: MAX_PINNED_BYTES / 1000 })}
          </p>
        )}
      </Panel>

      {canWrite && (
        <Panel title={t('pinned.addTitle')}>
          <div className="stack">
            <form className="row row--wrap row--tight" onSubmit={submit}>
              <input
                className="input"
                style={{ flex: '1 1 220px' }}
                value={name}
                maxLength={100}
                placeholder={t('pinned.newPlaceholder')}
                aria-label={t('pinned.newPlaceholder')}
                onChange={(e) => setName(e.target.value)}
              />
              <button type="submit" className="btn btn--primary btn--sm" disabled={!name.trim() || create.isPending}>
                <FilePlus2 size={13} /> {t('pinned.new')}
              </button>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => upload.current?.click()} disabled={send.isPending}>
                <Upload size={13} /> {t('pinned.upload')}
              </button>
              <input ref={upload} type="file" accept=".md,text/markdown" multiple hidden onChange={onFiles} />
            </form>

            {others.length > 0 && (
              <div className="row row--wrap row--tight">
                <select className="select" style={{ flex: '1 1 260px' }} value={choice} onChange={(e) => setChoice(e.target.value)} aria-label={t('pinned.pickExisting')}>
                  <option value="">{t('pinned.pickExisting')}</option>
                  {others.map((doc) => (
                    <option key={doc.id} value={doc.id}>{doc.path}</option>
                  ))}
                </select>
                <button type="button" className="btn btn--sm" disabled={!choice || toggle.isPending} onClick={() => toggle.mutate({ id: choice, pin: true })}>
                  <Pin size={13} /> {t('pinned.pin')}
                </button>
              </div>
            )}
          </div>
        </Panel>
      )}
    </div>
  )
}
